import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import {
  Sparkles, ArrowLeft, Users, CheckCircle, Clock, AlertTriangle,
  BarChart3, TrendingDown, Settings, CalendarDays, Filter, ShieldAlert,
  MessageCircle, Speaker, FileText, Quote, Ticket
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { format } from "date-fns";
import logger from "../lib/logger";
import { UserWaiverPanel } from "../components/UserWaiverPanel";
import { WaiverAuditTimeline } from "../components/WaiverAuditTimeline";
import { RecentErrorsPanel } from "../components/RecentErrorsPanel";
import { FaqInsightsPanel } from "../components/FaqInsightsPanel";
import { HandsFreeInsightsPanel } from "../components/HandsFreeInsightsPanel";
import { ContentEditorPanel } from "../components/ContentEditorPanel";
import { QuotesEditorPanel } from "../components/QuotesEditorPanel";
import { ReferralsInsightPanel } from "../components/ReferralsInsightPanel";
import { PromoWaitlistPanel } from "../components/PromoWaitlistPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AppMenu } from "../components/AppMenu";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_SHORT = {
  quiet_the_worry: "Worry",
  overcome_overwhelm: "Overwhelm",
  dial_down_tension: "Tension",
  healing_old_stories: "Old Stories",
  beat_stage_fright: "Stage Fright",
  fear_of_sleeplessness: "Sleep",
  shake_off_sadness: "Sadness",
  boost_self_worth: "Self-Worth",
  hold_boundaries: "Boundaries",
  relief_from_grief: "Grief",
  calm_your_anger: "Anger",
  enhance_performance: "Performance",
  new_habit_new_you: "New Habit",
  headache_relief: "Headache",
  nightmare_reset: "Nightmare"
};

const COLORS = [
  "#3090A8", "#78C0D8", "#E2B887", "#D98880", "#9F7AEA",
  "#6BB5A0", "#E8A87C", "#85C1E9", "#C39BD3", "#F0B27A",
  "#76D7C4", "#F1948A", "#AED6F1"
];

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  useSeo({ title: "Admin — Bilateral Calm", path: "/admin", noindex: true });

  const fetchAnalytics = useCallback(async (sd, ed) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sd) params.set("start_date", sd);
      if (ed) params.set("end_date", ed);
      const url = `${API}/admin/analytics${params.toString() ? '?' + params.toString() : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setData(res.data);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
      logger.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    fetchAnalytics(startDate, endDate);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    fetchAnalytics("", "");
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="bg-white rounded-2xl p-10 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 text-center max-w-md">
          <div className="w-16 h-16 bg-[#FEF2F0] rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-[#D98880]" />
          </div>
          <h2 className="font-outfit font-semibold text-xl text-[#1B2349] mb-2">Admin Access Required</h2>
          <p className="text-[#718096] mb-6">
            You don't have permission to view this page. Contact an administrator to request access.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="btn-primary" data-testid="back-to-dashboard-btn">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  const overview = data?.overview || {};
  const completionChartData = (data?.completions_by_type || []).map(d => ({
    name: INTERVENTION_SHORT[d.type] || d.type,
    completions: d.completions
  }));
  const niChartData = (data?.not_improving_by_type || []).map(d => ({
    name: INTERVENTION_SHORT[d.type] || d.type,
    count: d.not_improving_count,
    avgBefore: d.avg_distress_before,
    avgAfter: d.avg_distress_after,
    users: d.unique_users
  }));
  const distressData = (data?.distress_improvement || []).map(d => ({
    name: INTERVENTION_SHORT[d.type] || d.type,
    initial: d.avg_initial,
    final: d.avg_final,
    improvement: d.improvement
  }));
  const taskData = (data?.active_by_task || []).map(d => ({
    name: `Task ${d.task}`,
    sessions: d.count
  }));
  const recentEvents = data?.recent_not_improving || [];

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3090A8] rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-semibold text-xl text-[#1B2349]">Bilateral Calm</span>
            <span className="text-xs bg-[#78C0D8] text-white px-2 py-0.5 rounded-full font-medium">Admin</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-[#718096] hover:text-[#3090A8] transition-colors">Dashboard</Link>
            <Link to="/admin" className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors">Analytics</Link>
            <Link to="/settings" className="text-[#718096] hover:text-[#3090A8] transition-colors">Settings</Link>
          </nav>
          <AppMenu />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
          </button>
          <div>
            <h1 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349]">Admin Console</h1>
            <p className="text-[#718096]">Analytics, user management, and waiver audit trail</p>
          </div>
        </div>

        <Tabs defaultValue="analytics" className="w-full" data-testid="admin-tabs">
          <TabsList className="bg-white border border-slate-100 rounded-xl p-1 h-auto mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
            <TabsTrigger
              value="analytics"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-analytics"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-users"
            >
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-audit"
            >
              <ShieldAlert className="w-4 h-4 mr-2" />
              Audit Trail
            </TabsTrigger>
            <TabsTrigger
              value="faq"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-faq"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              FAQ Insights
            </TabsTrigger>
            <TabsTrigger
              value="hands-free"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-hands-free"
            >
              <Speaker className="w-4 h-4 mr-2" />
              Hands-free
            </TabsTrigger>
            <TabsTrigger
              value="content"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-content"
            >
              <FileText className="w-4 h-4 mr-2" />
              Content
            </TabsTrigger>
            <TabsTrigger
              value="quotes"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-quotes"
            >
              <Quote className="w-4 h-4 mr-2" />
              Quotes
            </TabsTrigger>
            <TabsTrigger
              value="referrals"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-referrals"
            >
              <Users className="w-4 h-4 mr-2" />
              Referrals
            </TabsTrigger>
            <TabsTrigger
              value="promo"
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#3090A8] data-[state=active]:text-white text-[#718096]"
              data-testid="tab-promo"
            >
              <Ticket className="w-4 h-4 mr-2" />
              VIP / Waitlist
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-0">
        {/* Date Range Filter */}
        <div className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-8" data-testid="date-filter">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-[#1B2349]">
              <CalendarDays className="w-5 h-5 text-[#78C0D8]" />
              <span className="font-medium text-sm">Date Range</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 flex-1">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-xl w-40 text-sm"
                placeholder="Start date"
                data-testid="filter-start-date"
              />
              <span className="text-[#718096] text-sm">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-xl w-40 text-sm"
                placeholder="End date"
                data-testid="filter-end-date"
              />
              <Button
                onClick={handleFilter}
                size="sm"
                className="btn-primary text-xs px-4"
                data-testid="apply-filter-btn"
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                Apply
              </Button>
              {(startDate || endDate) && (
                <Button
                  onClick={handleClearFilter}
                  variant="ghost"
                  size="sm"
                  className="text-[#718096] text-xs"
                  data-testid="clear-filter-btn"
                >
                  Clear
                </Button>
              )}
            </div>
            {data?.filters?.start_date && (
              <span className="text-xs text-[#3090A8] bg-[#E8F0E7] px-2 py-1 rounded-full">
                Filtered
              </span>
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Users", value: overview.total_users || 0, icon: <Users className="w-5 h-5" />, color: "#3090A8", bg: "#E8F0E7" },
            { label: "Completed", value: overview.total_completed || 0, icon: <CheckCircle className="w-5 h-5" />, color: "#78C0D8", bg: "#EAF0F9" },
            { label: "In Progress", value: overview.total_in_progress || 0, icon: <Clock className="w-5 h-5" />, color: "#E2B887", bg: "#FEF3E7" },
            { label: "Not Improving", value: overview.total_not_improving || 0, icon: <AlertTriangle className="w-5 h-5" />, color: "#D98880", bg: "#FEF2F0" }
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100">              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: card.bg, color: card.color }}>
                  {card.icon}
                </div>
                <div>
                  <p className="text-xs text-[#718096]">{card.label}</p>
                  <p className="font-outfit font-semibold text-2xl text-[#1B2349]">{card.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Not Improving by Intervention */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100" data-testid="ni-chart">
            <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#D98880]" />
              "Not Improving" by Intervention
            </h2>
            <p className="text-sm text-[#718096] mb-4">Which interventions users struggle with most</p>
            {niChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={niChartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#718096" }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#718096" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="count" fill="#D98880" radius={[6, 6, 0, 0]} name="Events" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#718096] text-sm">No "Not Improving" events yet</div>
            )}
          </div>

          {/* Distress Improvement */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100" data-testid="distress-chart">
            <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-[#3090A8]" />
              Distress Improvement (Completed)
            </h2>
            <p className="text-sm text-[#718096] mb-4">Average distress before vs after for completed interventions</p>
            {distressData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={distressData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#718096" }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#718096" }} domain={[0, 10]} />
                  <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="initial" fill="#D98880" radius={[6, 6, 0, 0]} name="Before" />
                  <Bar dataKey="final" fill="#3090A8" radius={[6, 6, 0, 0]} name="After" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#718096] text-sm">No completed interventions with distress data yet</div>
            )}
          </div>

          {/* Completions by Type */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100" data-testid="completions-chart">
            <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#78C0D8]" />
              Completions by Intervention
            </h2>
            <p className="text-sm text-[#718096] mb-4">Which interventions are most used</p>
            {completionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={completionChartData} cx="50%" cy="50%" outerRadius={100} dataKey="completions" nameKey="name" label={({ name, completions }) => `${name}: ${completions}`} labelLine={false}>
                    {completionChartData.map((entry, index) => (
                      <Cell key={entry.name || `cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E2E8F0" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#718096] text-sm">No completions yet</div>
            )}
          </div>

          {/* Active Sessions by Task */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100" data-testid="task-chart">
            <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#E2B887]" />
              Active Sessions by Task
            </h2>
            <p className="text-sm text-[#718096] mb-4">Where users currently are in their journey</p>
            {taskData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={taskData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#718096" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#718096" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="sessions" fill="#E2B887" radius={[6, 6, 0, 0]} name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#718096] text-sm">No active sessions</div>
            )}
          </div>
        </div>

        {/* Not Improving Detail Table */}
        {niChartData.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-8" data-testid="ni-table">
            <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-4">"Not Improving" Detail</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-[#718096] font-medium">Intervention</th>
                    <th className="text-center py-3 px-4 text-[#718096] font-medium">Events</th>
                    <th className="text-center py-3 px-4 text-[#718096] font-medium">Unique Users</th>
                    <th className="text-center py-3 px-4 text-[#718096] font-medium">Avg Distress Before</th>
                    <th className="text-center py-3 px-4 text-[#718096] font-medium">Avg Distress After</th>
                    <th className="text-center py-3 px-4 text-[#718096] font-medium">Avg Change</th>
                  </tr>
                </thead>
                <tbody>
                  {niChartData.map((row) => (
                    <tr key={row.name} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4 font-medium text-[#1B2349]">{row.name}</td>
                      <td className="py-3 px-4 text-center">{row.count}</td>
                      <td className="py-3 px-4 text-center">{row.users}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-semibold text-white ${row.avgBefore >= 7 ? 'bg-[#D98880]' : row.avgBefore >= 4 ? 'bg-[#E2B887]' : 'bg-[#3090A8]'}`}>
                          {row.avgBefore ?? '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-semibold text-white ${row.avgAfter >= 7 ? 'bg-[#D98880]' : row.avgAfter >= 4 ? 'bg-[#E2B887]' : 'bg-[#3090A8]'}`}>
                          {row.avgAfter ?? '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {row.avgBefore != null && row.avgAfter != null ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${row.avgBefore > row.avgAfter ? 'bg-[#E8F0E7] text-[#3090A8]' : 'bg-[#FEF2F0] text-[#D98880]'}`}>
                            {row.avgBefore > row.avgAfter ? '-' : '+'}{Math.abs(row.avgBefore - row.avgAfter).toFixed(1)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

          {/* Recent Not Improving Events */}
          {recentEvents.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100" data-testid="recent-events">
              <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-4">Recent "Not Improving" Events</h2>
              <div className="space-y-3">
                {recentEvents.map((ev) => (
                  <div key={ev.event_id || ev.created_at} className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50/50">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-4 h-4 text-[#E2B887]" />
                      <span className="text-sm font-medium text-[#1B2349]">
                        {INTERVENTION_SHORT[ev.intervention_type] || ev.intervention_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#718096]">
                      {ev.distress_before != null && (
                        <span>Before: <strong>{ev.distress_before}</strong></span>
                      )}
                      {ev.distress_after != null && (
                        <span>After: <strong>{ev.distress_after}</strong></span>
                      )}
                      <span>{format(new Date(ev.created_at), 'MMM d, h:mm a')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

          <TabsContent value="users" className="mt-0">
            <UserWaiverPanel />
          </TabsContent>

          <TabsContent value="audit" className="mt-0">
            <WaiverAuditTimeline />
            <RecentErrorsPanel />
          </TabsContent>

          <TabsContent value="faq" className="mt-0">
            <FaqInsightsPanel />
          </TabsContent>
          <TabsContent value="hands-free" className="mt-0">
            <HandsFreeInsightsPanel />
          </TabsContent>
          <TabsContent value="content" className="mt-0">
            <ContentEditorPanel />
          </TabsContent>
          <TabsContent value="quotes" className="mt-0">
            <QuotesEditorPanel />
          </TabsContent>
          <TabsContent value="referrals" className="mt-0">
            <ReferralsInsightPanel />
          </TabsContent>
          <TabsContent value="promo" className="mt-0">
            <PromoWaitlistPanel code="CALM100" />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
